/**
 * Plugin Theme Registry
 *
 * Frontend registry for plugin-provided themes.
 * Works in conjunction with backend PluginThemeRegistryService.
 * Part of Phase 0: Core Preparation for Modularization.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Theme type
 */
export type ThemeType = 'light' | 'dark' | 'auto';

/**
 * Color palette definition
 */
export interface ThemeColorPalette {
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  background: string;
  foreground: string;
  muted: string;
  mutedForeground: string;
  card: string;
  cardForeground: string;
  border: string;
  input: string;
  ring: string;
  destructive: string;
  destructiveForeground: string;
  success: string;
  successForeground: string;
  warning: string;
  warningForeground: string;
}

/**
 * Theme definition
 */
export interface ThemeDefinition {
  /** Unique theme ID */
  id: string;
  /** Plugin that owns this theme */
  pluginId: string;
  /** Human-readable name */
  name: string;
  /** Description */
  description?: string;
  /** Theme type */
  type: ThemeType;
  /** Preview image URL */
  preview?: string;
  /** Color palette */
  colors: Partial<ThemeColorPalette>;
  /** Additional CSS variables */
  cssVariables?: Record<string, string>;
  /** Custom CSS */
  customCss?: string;
  /** Font configuration */
  fonts?: {
    sans?: string;
    mono?: string;
    heading?: string;
  };
  /** Border radius */
  borderRadius?: string;
  /** Whether theme is enabled */
  enabled: boolean;
}

/**
 * Theme registry state
 */
interface ThemeRegistryState {
  /** Map of theme ID to definition */
  themes: Map<string, ThemeDefinition>;
  /** Currently active theme ID */
  activeThemeId: string | null;
  /** User's preferred color scheme */
  preferredColorScheme: 'light' | 'dark' | 'system';
  /** Injected CSS */
  injectedStyles: Map<string, string>;
  
  // Actions
  registerTheme: (theme: ThemeDefinition) => void;
  unregisterTheme: (themeId: string) => void;
  unregisterPluginThemes: (pluginId: string) => void;
  activateTheme: (themeId: string) => void;
  deactivateTheme: () => void;
  setPreferredColorScheme: (scheme: 'light' | 'dark' | 'system') => void;
  injectCss: (id: string, css: string) => void;
  removeCss: (id: string) => void;
  getTheme: (themeId: string) => ThemeDefinition | undefined;
  getActiveTheme: () => ThemeDefinition | undefined;
  getAllThemes: () => ThemeDefinition[];
  getThemesByType: (type: ThemeType) => ThemeDefinition[];
  applyTheme: () => void;
  syncFromBackend: (themes: ThemeDefinition[], activeId?: string | null) => void;
}

/**
 * Convert camelCase to kebab-case
 */
function toKebabCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Apply theme to document
 */
function applyThemeToDocument(theme: ThemeDefinition | undefined, injectedStyles: Map<string, string>) {
  const root = document.documentElement;
  
  // Clear existing theme classes
  root.classList.remove('theme-light', 'theme-dark');
  
  if (theme) {
    // Add theme class
    if (theme.type === 'light') {
      root.classList.add('theme-light');
    } else if (theme.type === 'dark') {
      root.classList.add('theme-dark');
    }
    
    // Apply color variables
    for (const [key, value] of Object.entries(theme.colors)) {
      root.style.setProperty(`--${toKebabCase(key)}`, value);
    }
    
    // Apply custom CSS variables
    if (theme.cssVariables) {
      for (const [key, value] of Object.entries(theme.cssVariables)) {
        const varName = key.startsWith('--') ? key : `--${key}`;
        root.style.setProperty(varName, value);
      }
    }
    
    // Apply fonts
    if (theme.fonts) {
      if (theme.fonts.sans) {
        root.style.setProperty('--font-sans', theme.fonts.sans);
      }
      if (theme.fonts.mono) {
        root.style.setProperty('--font-mono', theme.fonts.mono);
      }
      if (theme.fonts.heading) {
        root.style.setProperty('--font-heading', theme.fonts.heading);
      }
    }
    
    // Apply border radius
    if (theme.borderRadius) {
      root.style.setProperty('--radius', theme.borderRadius);
    }
  }
  
  // Apply/update injected styles
  let styleElement = document.getElementById('plugin-theme-styles');
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = 'plugin-theme-styles';
    document.head.appendChild(styleElement);
  }
  
  const allCss: string[] = [];
  
  // Add theme custom CSS
  if (theme?.customCss) {
    allCss.push(theme.customCss);
  }
  
  // Add injected styles
  for (const css of injectedStyles.values()) {
    allCss.push(css);
  }
  
  styleElement.textContent = allCss.join('\n\n');
}

/**
 * Theme registry store
 */
export const useThemeRegistry = create<ThemeRegistryState>()(
  persist(
    (set, get) => ({
      themes: new Map(),
      activeThemeId: null,
      preferredColorScheme: 'system',
      injectedStyles: new Map(),

      registerTheme: (theme: ThemeDefinition) => {
        set((state) => {
          const newThemes = new Map(state.themes);
          newThemes.set(theme.id, theme);
          return { themes: newThemes };
        });
      },

      unregisterTheme: (themeId: string) => {
        set((state) => {
          const newThemes = new Map(state.themes);
          newThemes.delete(themeId);
          
          // Deactivate if this was the active theme
          const newActiveId = state.activeThemeId === themeId ? null : state.activeThemeId;
          
          return { themes: newThemes, activeThemeId: newActiveId };
        });
        
        // Re-apply theme
        get().applyTheme();
      },

      unregisterPluginThemes: (pluginId: string) => {
        set((state) => {
          const newThemes = new Map(state.themes);
          const newInjected = new Map(state.injectedStyles);
          let newActiveId = state.activeThemeId;
          
          for (const [id, theme] of newThemes) {
            if (theme.pluginId === pluginId) {
              newThemes.delete(id);
              if (state.activeThemeId === id) {
                newActiveId = null;
              }
            }
          }
          
          // Remove injected styles from this plugin
          for (const [id] of newInjected) {
            if (id.startsWith(`${pluginId}:`)) {
              newInjected.delete(id);
            }
          }
          
          return { themes: newThemes, activeThemeId: newActiveId, injectedStyles: newInjected };
        });
        
        get().applyTheme();
      },

      activateTheme: (themeId: string) => {
        const theme = get().themes.get(themeId);
        if (!theme || !theme.enabled) return;
        
        set({ activeThemeId: themeId });
        get().applyTheme();
      },

      deactivateTheme: () => {
        set({ activeThemeId: null });
        get().applyTheme();
      },

      setPreferredColorScheme: (scheme: 'light' | 'dark' | 'system') => {
        set({ preferredColorScheme: scheme });
        get().applyTheme();
      },

      injectCss: (id: string, css: string) => {
        set((state) => {
          const newInjected = new Map(state.injectedStyles);
          newInjected.set(id, css);
          return { injectedStyles: newInjected };
        });
        get().applyTheme();
      },

      removeCss: (id: string) => {
        set((state) => {
          const newInjected = new Map(state.injectedStyles);
          newInjected.delete(id);
          return { injectedStyles: newInjected };
        });
        get().applyTheme();
      },

      getTheme: (themeId: string) => {
        return get().themes.get(themeId);
      },

      getActiveTheme: () => {
        const { activeThemeId, themes } = get();
        if (!activeThemeId) return undefined;
        return themes.get(activeThemeId);
      },

      getAllThemes: () => {
        return Array.from(get().themes.values()).filter(t => t.enabled);
      },

      getThemesByType: (type: ThemeType) => {
        return get().getAllThemes().filter(t => t.type === type);
      },

      applyTheme: () => {
        const { activeThemeId, themes, injectedStyles } = get();
        const theme = activeThemeId ? themes.get(activeThemeId) : undefined;
        applyThemeToDocument(theme, injectedStyles);
      },

      syncFromBackend: (themes: ThemeDefinition[], activeId?: string | null) => {
        set(() => {
          const newThemes = new Map<string, ThemeDefinition>();
          
          for (const theme of themes) {
            newThemes.set(theme.id, theme);
          }
          
          return { 
            themes: newThemes,
            activeThemeId: activeId !== undefined ? activeId : null,
          };
        });
        
        get().applyTheme();
      },
    }),
    {
      name: 'neuro-theme-preferences',
      partialize: (state) => ({
        activeThemeId: state.activeThemeId,
        preferredColorScheme: state.preferredColorScheme,
      }),
    }
  )
);

/**
 * Hook to get current theme
 */
export function useCurrentTheme() {
  return useThemeRegistry((state) => state.getActiveTheme());
}

/**
 * Hook to get available themes
 */
export function useAvailableThemes() {
  return useThemeRegistry((state) => state.getAllThemes());
}

/**
 * Initialize theme on app load
 */
export function initializeTheme() {
  const state = useThemeRegistry.getState();
  state.applyTheme();
}
